import React, { useCallback, useState } from 'react';
import { AppButton, Box, Text } from '@common-fe/common-fe';

import { PlanCodeField } from '@/components/controls/PlanCodeField';
import { HealthPlanRenewal, RenewalDeductibleConfig } from '@/modules/Renewals/renewals.types';
import useHealthPlansStore from '@/modules/Renewals/RenewalSetup/stores/useHealthPlans.store';

import { EditAmountsModalContainer } from '../EditAmountsModal';
import { EditAmountsFormValues } from '../EditAmountsModal/EditAmountsModal.types';
import convertFormValuesToHealthPlanRenewal from '../EditAmountsModal/utils/convertFormValuesToHealthPlanRenewal';

import HealthPlanName from './components/HealthPlanName';
import {
  HealthPlanField,
  useEditableHealthPlan,
  useHealthPlanCodeActions,
  useHealthPlanFieldError,
} from './hooks/useHealthPlanCodeActions';
import { CoverageSection, TierSection } from './components';
import { DEFAULT_TEST_ID } from './HealthPlanSummaryCard.constants';

interface Props {
  healthPlan: HealthPlanRenewal;
  testId?: string;
  showEditButtons?: boolean;
  showError?: boolean;
  planYearDates?: string;
  originalDeductibleConfigs?: RenewalDeductibleConfig[];
  showEditedPlanCode?: boolean;
}

const HealthPlanSummaryCard: React.FC<Props> = ({
  healthPlan,
  testId = DEFAULT_TEST_ID,
  showEditButtons = true,
  showError = false,
  planYearDates = '',
  originalDeductibleConfigs,
  showEditedPlanCode = false,
}) => {
  const { updateEntityDebounced, resetEntity } = useHealthPlanCodeActions();
  const editableHealthPlan = useEditableHealthPlan(healthPlan.id);
  const currentPlanCode = editableHealthPlan?.planCode;
  const errorText = useHealthPlanFieldError(healthPlan.id, HealthPlanField.PlanCode);
  const [isEditAmountsVisible, setEditAmountsVisible] = useState(false);
  const setState = useHealthPlansStore((state) => state.setState);
  const healthPlans = useHealthPlansStore((state) => state.state.healthPlans);

  const handleOpenEditAmounts = useCallback(() => {
    setEditAmountsVisible(true);
  }, []);

  const handleCloseEditAmounts = useCallback(() => {
    setEditAmountsVisible(false);
  }, []);

  const handleSaveEditAmounts = useCallback(
    (values: EditAmountsFormValues) => {
      if (healthPlan.id && healthPlans) {
        const updatedHealthPlans = (healthPlans || []).map((hp) =>
          hp.id === healthPlan.id ? convertFormValuesToHealthPlanRenewal(hp, values) : hp
        );
        setState({ healthPlans: updatedHealthPlans });
      }
      setEditAmountsVisible(false);
    },
    [healthPlan.id, healthPlans, setState]
  );

  return (
    <Box
      data-testid={testId}
      pad={{ vertical: 'spacing16', horizontal: 'spacing24' }}
      border={{ size: 'small', color: 'border1' }}
      round="container2Round"
      background="canvas"
    >
      {/* Health Plan Name */}
      <Box margin={{ bottom: 'spacing12' }}>
        <HealthPlanName
          healthPlan={healthPlan}
          showError={showError}
          isViewMode={!showEditButtons}
          showEditAction={showEditButtons}
          testId={`${testId}_name`}
        />
      </Box>
      {/* Plan Code */}
      <Box margin={{ bottom: 'spacing12' }}>
        <PlanCodeField
          planCode={showEditedPlanCode ? (currentPlanCode ?? healthPlan.planCode) : currentPlanCode}
          defaultPlanCode={healthPlan.defaultPlanCode ?? healthPlan.planCode}
          onChange={(value) => {
            if (healthPlan.id) updateEntityDebounced(healthPlan.id, HealthPlanField.PlanCode, value);
          }}
          onReset={() => {
            if (healthPlan.id) resetEntity(healthPlan.id, HealthPlanField.PlanCode);
          }}
          showEditAction={showEditButtons}
          isViewMode={showEditedPlanCode ? false : !showEditButtons}
          showError={showError && !!errorText}
          errorText={errorText}
          testId={`${testId}_plan-code`}
        />
      </Box>
      {/* Tiers Section */}
      {healthPlan.processingTiers && healthPlan.processingTiers.length > 0 && (
        <Box margin={{ bottom: 'spacing12' }}>
          <Text size="small" color="textSecondary" weight={500} margin={{ bottom: 'spacing4' }}>
            Tiers
          </Text>
          <Box>
            {healthPlan.processingTiers.map((tier, index) => (
              <TierSection
                key={`${tier.name}-${index}`}
                tier={tier}
                index={index}
                isLast={index === healthPlan.processingTiers!.length - 1}
                testId={testId}
                deductibleConfigs={healthPlan.deductibleConfigs}
              />
            ))}
          </Box>
        </Box>
      )}
      {/* Coverage Levels Section */}
      {healthPlan.deductibleConfigs && healthPlan.deductibleConfigs.length > 0 && (
        <Box>
          <Box height="1px" background="border1" margin={{ bottom: 'spacing12' }} />
          <Text size="small" color="textSecondary" weight={500} margin={{ bottom: 'spacing4' }}>
            Coverage levels
          </Text>
          <Box>
            {healthPlan.deductibleConfigs.map((config, index) => (
              <CoverageSection
                key={config.coverageId}
                config={config}
                isLast={index === healthPlan.deductibleConfigs!.length - 1}
                testId={testId}
              />
            ))}
          </Box>
        </Box>
      )}
      {/* Edit Amounts Button */}
      {showEditButtons && (
        <Box margin={{ top: 'spacing16' }}>
          <AppButton
            buttonType="secondary"
            size="L"
            width="100%"
            testId={`${testId}_edit-amounts-button`}
            onClick={handleOpenEditAmounts}
          >
            Edit Amounts
          </AppButton>
        </Box>
      )}

      <EditAmountsModalContainer
        visible={isEditAmountsVisible}
        healthPlan={healthPlan}
        planYearDates={planYearDates}
        onClose={handleCloseEditAmounts}
        onSave={handleSaveEditAmounts}
        originalDeductibleConfigs={originalDeductibleConfigs}
      />
    </Box>
  );
};

export default HealthPlanSummaryCard;
